/**
 * `runOncePerOrigin` — a one-time migration that stays one-time even though the
 * origin now hosts several builds of the app at once.
 *
 * ## The problem this exists for
 *
 * The web host serves the default build at `/` and any number of
 * named channels at `/<channel>/` (`scripts/deploy-client-dev.sh`). Those are
 * independent builds of DIFFERENT versions of the app — that is the point of a
 * channel — but `localStorage` is scoped to the ORIGIN, not the path, so all of
 * them share one keyspace.
 *
 * A start-up sweep that deletes keys "nothing reads any more" is therefore a
 * statement about ONE build. Run unconditionally, a newer channel deletes keys
 * an older channel is still actively using, every time the newer one is loaded:
 * the developer switches tabs, and the old build has silently lost its dev
 * settings or its uploaded prompt bodies. Before channels existed there was only
 * ever one build on the origin, so "run it on every boot" was harmless and this
 * hazard did not exist.
 *
 * A marker fixes it precisely, without weakening the migration: the sweep still
 * runs — once, for whichever build loads first — and after that the older build
 * keeps whatever it writes. What the migration is actually for (clearing a
 * profile that predates the move to `app-settings.json`) is fully served by
 * running once.
 *
 * ## Contract
 *
 * `id` names the migration, not the build. Changing it re-runs the sweep for
 * everyone, which is what you want when the set of keys being retired genuinely
 * grows — and is why the id should describe the retirement, not carry a version
 * number that invites a bump on every unrelated edit.
 *
 * Never throws: a browser refusing storage has nothing stored to migrate, so the
 * sweep is skipped rather than retried forever.
 */
const K_MARKER_PREFIX = "Campfire.Migration.";

export function runOncePerOrigin(id: string, migrate: () => void): void {
  const marker = `${K_MARKER_PREFIX}${id}`;
  try {
    if (window.localStorage.getItem(marker)) return;
  } catch {
    return;
  }
  migrate();
  try {
    // Written AFTER the sweep, so a migration that throws part-way is retried
    // on the next boot rather than being recorded as done.
    window.localStorage.setItem(marker, "1");
  } catch {
    // Storage refused the write; the sweep simply runs again next time.
  }
}
